/**
 * T-050 — the one measured answer to "how much of the bottom-right corner is
 * already taken?".
 *
 * Three unrelated things float over the bottom-right of a Uniflo page and none
 * of them could see the others: the clan workspace's To-do widget
 * (`clanRedesign/index.jsx`, `clan-redesign-todo-widget`), the floating mini
 * chat windows (`components/chat/ChatPopupManager.jsx`) and the "a new version
 * is ready" prompt (`components/shared/AppUpdatePrompt.jsx`). On the owner's
 * real board the open To-do widget sat on top of the fourth deck and an open
 * chat sat on top of the To-do widget.
 *
 * The workspace MEASURES its widget at runtime (a ResizeObserver, never a
 * hardcoded width — the widget is 326px open, 248px collapsed, moves to
 * `right: 20px` under 1180px and leaves the corner entirely under 640px) and
 * publishes how far in from the right edge of the viewport it reaches. Anything
 * else that wants to dock in that corner subtracts this first.
 *
 * The value is in CSS pixels measured from the RIGHT edge of the viewport
 * (`window.innerWidth - widgetRect.left`), so a consumer docks clear of it with
 * `right: reserved + gap`. It is 0 whenever no such widget is on screen — which
 * is every page outside the clan workspace, and the workspace itself below
 * 640px where the widget stops floating and joins the page flow.
 *
 * This is a module-scoped store rather than React context on purpose: the
 * publisher (the clan workspace page) and the subscribers (the app-root chat
 * manager and update prompt) sit in different branches of the tree with no
 * common provider, and adding one would mean editing the app root for a piece
 * of pure layout bookkeeping.
 */

let reservedRightPx = 0;
const listeners = new Set();

/** How far in from the viewport's right edge the floating dock reaches, in px. */
export function getDockReservedRight() {
  return reservedRightPx;
}

/**
 * Publish a new measurement. Called by whatever owns the floating widget;
 * pass 0 (or nothing) when it unmounts so the corner is released again.
 */
export function setDockReservedRight(next) {
  const value = Number.isFinite(Number(next)) && Number(next) > 0 ? Math.round(Number(next)) : 0;
  if (value === reservedRightPx) return;
  reservedRightPx = value;
  listeners.forEach((listener) => {
    try {
      listener(value);
    } catch (_error) {
      // A broken subscriber must not stop the others being told.
    }
  });
}

/** Subscribe to changes. Returns an unsubscribe function (useSyncExternalStore shape). */
export function subscribeDockReservedRight(listener) {
  if (typeof listener !== "function") return () => {};
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Test seam: forget every subscriber and reset the measurement. */
export function resetDockReservedRight() {
  reservedRightPx = 0;
  listeners.clear();
}
